// ---------------------------------------------------------------------------
// GraphCanvas — @xyflow/react + d3-force viewer for the App Graph.
//
// Styling inspired by opentology.dev: dark canvas, small glowing circular
// nodes, labels rendered below each node (not inside), thin near-transparent
// edges, force-directed physics layout via d3-force.
//
// Physics: `forceManyBody` (repulsion), `forceLink` (springs for edges),
// `forceCenter` (keep graph centered), `forceCollide` (avoid overlap).
// Simulation ticks are clamped to ~120 frames — good enough for a few
// hundred nodes to settle before the user interacts.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react'
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
  useEdgesState,
  useNodesState,
  type AriaLabelConfig,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type Simulation,
  type SimulationNodeDatum,
} from 'd3-force'
import { useTheme } from '@/hooks/use-theme'
import { useStudioLocale } from '@/i18n'
import type { AppGraph, AppGraphNodeType } from './types'
import { NODE_TYPE_COLOR } from './node-style'

export interface GraphCanvasProps {
  graph: AppGraph
  selectedId: string | null
  onSelect: (id: string | null) => void
  /** When non-null, non-matching nodes fade out and their edges are hidden. */
  typeFilter: AppGraphNodeType | null
}

// ── Force-directed layout via d3-force ──────────────────────────────────

interface LaidOutNode {
  id: string
  type: AppGraphNodeType
  label: string
  x: number
  y: number
}

interface SimNode extends SimulationNodeDatum {
  id: string
  type: AppGraphNodeType
  label: string
}

function runSimulation(graph: AppGraph): LaidOutNode[] {
  const simNodes: SimNode[] = graph.nodes.map((n) => ({
    id: n.id,
    type: n.type,
    label: n.label,
  }))
  const simLinks = graph.edges.map((e) => ({
    source: e.source,
    target: e.target,
  }))

  const sim: Simulation<SimNode, typeof simLinks[number]> = forceSimulation(simNodes)
    .force(
      'link',
      forceLink<SimNode, typeof simLinks[number]>(simLinks)
        .id((n) => n.id)
        .distance(80)
        .strength(0.25),
    )
    .force('charge', forceManyBody<SimNode>().strength(-220))
    .force('center', forceCenter(0, 0))
    .force('collide', forceCollide<SimNode>(18))
    .stop()

  // Tick to convergence synchronously.
  for (let i = 0; i < 180; i++) sim.tick()

  return simNodes.map((n) => ({
    id: n.id,
    type: n.type,
    label: n.label,
    x: n.x ?? 0,
    y: n.y ?? 0,
  }))
}

// ── Custom node — small glowing dot with label below ────────────────────

function GraphDot({ data }: NodeProps) {
  const d = data as {
    label: string
    type: AppGraphNodeType
    dimmed: boolean
    selected: boolean
    isDark: boolean
  }
  const color = NODE_TYPE_COLOR[d.type]
  const size = d.selected ? 16 : 12
  const labelColor = d.isDark
    ? 'rgba(229, 231, 235, 0.85)'
    : 'rgba(24, 24, 27, 0.85)'
  const selectedOutline = d.isDark ? '#ffffffcc' : '#000000aa'
  // xyflow v12 requires both target and source Handles on a custom node for
  // edges to render. Without these, ReactFlow reports zero handle positions
  // and every edge collapses to a zero-length line. The handles are styled
  // to be invisible (no background, no border, zero-sized) so the node's
  // visual shape stays exactly as the glowing dot. `connectable={false}`
  // keeps them as read-only endpoints — users can't draw new edges.
  const handleStyle = {
    width: 1,
    height: 1,
    minWidth: 1,
    minHeight: 1,
    background: 'transparent',
    border: 'none',
    opacity: 0,
  } as const
  return (
    <div
      className="flex flex-col items-center"
      style={{
        opacity: d.dimmed ? 0.2 : 1,
        transition: 'opacity 120ms ease',
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={handleStyle}
        isConnectable={false}
      />
      <div
        aria-hidden
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: color,
          boxShadow: `0 0 ${d.selected ? 16 : 10}px ${color}`,
          outline: d.selected ? `2px solid ${selectedOutline}` : 'none',
          outlineOffset: 2,
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={handleStyle}
        isConnectable={false}
      />
      <span
        className="pointer-events-none mt-1 max-w-[110px] truncate text-[10px] leading-tight"
        style={{ color: labelColor }}
        title={d.label}
      >
        {d.label}
      </span>
    </div>
  )
}

const NODE_TYPES = { dot: GraphDot }

// ── Canvas ───────────────────────────────────────────────────────────────

export function GraphCanvas({
  graph,
  selectedId,
  onSelect,
  typeFilter,
}: GraphCanvasProps) {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'
  const bg = isDark ? '#0a0a0b' : '#fafafa'
  const gridColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)'
  const edgeStroke = isDark ? 'rgba(156, 163, 175, 0.35)' : 'rgba(82, 82, 91, 0.35)'

  // Recompute positions whenever the node/edge set changes; skip the run
  // when the ids match to avoid jitter on unrelated re-renders (e.g. filter
  // toggles).
  const signature = useMemo(
    () =>
      graph.nodes
        .map((n) => n.id)
        .concat(graph.edges.map((e) => e.id))
        .join('|'),
    [graph],
  )
  const [laidOut, setLaidOut] = useState<LaidOutNode[]>([])
  useEffect(() => {
    setLaidOut(runSimulation(graph))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])

  const flowNodes: Node[] = useMemo(
    () =>
      laidOut.map((n) => ({
        id: n.id,
        type: 'dot',
        position: { x: n.x, y: n.y },
        data: {
          label: n.label,
          type: n.type,
          dimmed: typeFilter !== null && n.type !== typeFilter,
          selected: n.id === selectedId,
          isDark,
        },
        selectable: true,
        draggable: true,
      })),
    [laidOut, selectedId, typeFilter, isDark],
  )

  const flowEdges: Edge[] = useMemo(
    () =>
      graph.edges
        .filter((e) => {
          if (typeFilter === null) return true
          const srcType = graph.nodes.find((n) => n.id === e.source)?.type
          const tgtType = graph.nodes.find((n) => n.id === e.target)?.type
          return srcType === typeFilter || tgtType === typeFilter
        })
        .map((e) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          type: 'straight',
          style: {
            strokeWidth: 0.6,
            stroke: edgeStroke,
          },
        })),
    [graph.edges, graph.nodes, typeFilter, edgeStroke],
  )

  const { strings } = useStudioLocale()
  // React Flow ships its accessible names and keyboard descriptions in
  // English and localizes them ONLY through this prop. The keys come from
  // `@xyflow/system`'s `defaultAriaLabelConfig`; the prop is
  // `Partial<AriaLabelConfig>`, so a key the library renames stops being
  // honoured silently — hence `satisfies`, which makes that a type error.
  const canvasCopy = strings.appMap.canvas
  const ariaLabelConfig = {
    'controls.ariaLabel': canvasCopy.controls,
    'controls.zoomIn.ariaLabel': canvasCopy.zoomIn,
    'controls.zoomOut.ariaLabel': canvasCopy.zoomOut,
    'controls.fitView.ariaLabel': canvasCopy.fitView,
    'controls.interactive.ariaLabel': canvasCopy.interactive,
    'minimap.ariaLabel': canvasCopy.miniMap,
    'handle.ariaLabel': canvasCopy.handle,
    'node.a11yDescription.default': canvasCopy.nodeDescription,
    'node.a11yDescription.keyboardDisabled': canvasCopy.nodeKeyboardDisabled,
    'node.a11yDescription.ariaLiveMessage': ({ direction, x, y }: { direction: string; x: number; y: number }) =>
      canvasCopy.nodeMoved(direction, x, y),
    'edge.a11yDescription.default': canvasCopy.edgeDescription,
  } satisfies Partial<AriaLabelConfig>

  const [nodes, setNodes, onNodesChange] = useNodesState(flowNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowEdges)

  useEffect(() => {
    setNodes(flowNodes)
  }, [flowNodes, setNodes])
  useEffect(() => {
    setEdges(flowEdges)
  }, [flowEdges, setEdges])

  return (
    <div
      className="h-full w-full"
      data-testid="app-graph-canvas"
      style={{ backgroundColor: bg }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={(_, node) => onSelect(node.id)}
        onPaneClick={() => onSelect(null)}
        fitView
        minZoom={0.2}
        maxZoom={2.5}
        proOptions={{ hideAttribution: true }}
        ariaLabelConfig={ariaLabelConfig}
      >
        <Background gap={24} size={1} color={gridColor} />
        <Controls
          showInteractive={false}
          className={
            isDark
              ? '!bg-zinc-900/80 [&>button]:!border-zinc-700 [&>button]:!bg-zinc-800 [&>button]:!text-zinc-300 [&>button]:!fill-zinc-300'
              : '!bg-white/90 [&>button]:!border-zinc-300 [&>button]:!bg-white [&>button]:!text-zinc-700 [&>button]:!fill-zinc-700'
          }
        />
        <MiniMap
          pannable
          zoomable
          nodeColor={(n) => {
            const data = n.data as { type?: AppGraphNodeType } | undefined
            return data?.type ? NODE_TYPE_COLOR[data.type] : '#888'
          }}
          className={isDark ? '!bg-zinc-900/80' : '!bg-zinc-100/90'}
          maskColor={isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.55)'}
        />
      </ReactFlow>
    </div>
  )
}
